import { useInfiniteQuery, type UseInfiniteQueryResult } from '@tanstack/react-query';

import { SUPPORTED_CHAIN_IDS } from '@/constants/chain';

import { TokensByNameOrSymbolQuery } from '../utils/0x/token-registry.types';
import { fetcher, getFullURL, handleResponse } from '../utils/fetcher';
import { isChainAddress } from '../utils/models';
import { routes } from '../utils/routes';
import {
  searchTrendWalletsKey,
  type SearchTokensPage,
  type SearchTrendLookupResponse,
  type TrendSource,
  type TrendWallets,
} from '../utils/search-trends';
import { objectToQueryString } from '../utils/string';

const DEFAULT_RESULTS_LIMIT = 10;

type UseSearchTokensParams = {
  query?: string;
  chainIds?: number[];
  limit?: number;
  enabled?: boolean;
  trend?: { wallets: TrendWallets; source: TrendSource };
};

type UseSearchTokensArgs = UseSearchTokensParams & {
  effectiveLimit: number;
};

const buildUrl = ({ query, chainIds, effectiveLimit }: UseSearchTokensArgs, page: number) => {
  const parameters: TokensByNameOrSymbolQuery = {
    chainId: chainIds,
    limit: effectiveLimit,
    page, // 0x API uses 1-based pagination
    query: query ?? '',
  };

  return `${routes.api.SEARCH_TOKENS}${objectToQueryString(parameters)}`;
};

/** Scopes tracked receipts to their requesting wallets and search surface. */
export const searchTokensQueryKey = ({ query, chainIds, limit, trend }: UseSearchTokensParams) =>
  [
    'search-tokens',
    {
      query,
      chainIds,
      limit,
      ...(trend
        ? { trend: { walletsKey: searchTrendWalletsKey(trend.wallets), source: trend.source } }
        : {}),
    },
  ] as const;

/** Keeps ordinary searches cached while refreshing tracked receipts on reactivation. */
export const useSearchTokens = ({
  query,
  chainIds,
  limit = DEFAULT_RESULTS_LIMIT,
  enabled = true,
  trend,
}: UseSearchTokensParams): UseInfiniteQueryResult<
  { pages: SearchTokensPage[]; pageParams: number[] },
  Error
> & { fetchNextPage: () => Promise<unknown> } => {
  const wallets = trend
    ? { ethereum: trend.wallets.ethereum?.toLowerCase(), solana: trend.wallets.solana }
    : undefined;
  const source = trend?.source;

  return useInfiniteQuery<
    SearchTokensPage,
    Error,
    { pages: SearchTokensPage[]; pageParams: number[] },
    ReturnType<typeof searchTokensQueryKey>,
    number
  >({
    queryKey: searchTokensQueryKey({ query, chainIds, limit, trend }),
    queryFn: async (context) => {
      if (!wallets || !source) {
        return fetcher<SearchTokensPage>(
          buildUrl({ query, chainIds, effectiveLimit: limit }, context.pageParam),
        );
      }

      const { results, receipt } = await fetch(getFullURL(routes.api.SEARCH_TOKENS), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          chainIds: chainIds ?? SUPPORTED_CHAIN_IDS,
          limit,
          page: context.pageParam,
          wallets,
          source,
        }),
        cache: 'no-store',
        signal: context.signal,
      }).then(handleResponse<SearchTrendLookupResponse>);

      return receipt
        ? { ...results, searchReceipt: { ...receipt, walletsKey: searchTrendWalletsKey(wallets) } }
        : results;
    },
    initialPageParam: 1,
    // Bump the page number until the API returns fewer than `limit` items.
    getNextPageParam: (lastPage, allPages) => {
      if (trend && isChainAddress(query?.trim())) return undefined;
      if ('data' in lastPage && lastPage.data && lastPage.data.length >= limit) {
        return allPages.length + 1;
      }
      return undefined;
    },
    enabled: enabled && !!query?.trim(),
    staleTime: trend ? 0 : Infinity,
    ...(trend ? { refetchOnMount: 'always' as const, refetchOnWindowFocus: false } : {}),
    refetchOnReconnect: false,
  });
};
